import { inject, Injectable, OnDestroy } from '@angular/core';
import { ConfigInstanceToken } from '../../interfaces/config.interface';
import { OdataOtherTasksService } from './odata-other-tasks.service';
import { EMPTY, exhaustMap, Observable, of, Subject, Subscription } from 'rxjs';
import { OtherTaskNotificationModel } from '../../models/low-level/other-task-notification.model';
import { NotificationToGetOtherTasks, NotificationToModifyOtherTask } from '../../models/low-level/notification.model';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import moment from 'moment';
import { TimeUtilities } from '../../utilities/time-utilities';
import { OtherTasksDexieDbService } from './other-tasks-dexie-db.service';
import { LogInstanceToken } from '@evorait/pace-logging';
import { ConcurrentUsageService } from '../concurrent-usage/concurrent-usage.service';
import { LEGACY_CONCURRENT_USAGE_KEYS } from '../concurrent-usage/concurrent-usage-keys';
import { catchError, concatMap, filter, map, take, tap } from 'rxjs/operators';
import { OtherTaskOriginalDate } from './odata-other-tasks.interface';
import { ConnectivityService } from '../connectivity/connectivity.service';

@Injectable({
    providedIn: 'root',
})
export class OtherTasksService implements OnDestroy {
    private readonly db = inject(OtherTasksDexieDbService);
    private readonly log = inject(LogInstanceToken);
    private readonly configService = inject(ConfigInstanceToken);
    private readonly connectivityService = inject(ConnectivityService);
    private readonly odataOtherTasksService = inject(OdataOtherTasksService);
    private readonly concurrentUsageService = inject(ConcurrentUsageService);

    private static readonly OTHER_TASKS_CONCURRENT_USAGE_KEY = LEGACY_CONCURRENT_USAGE_KEYS.otherTasksOriginalDates;
    private persistentSyncSubscription: Subscription;
    private syncRequests = new Subject<void>();
    public syncCompleted$ = new Subject<void>();

    /**
     * Start the background synchronization process immediately.
     * This creates a persistent, application-wide listener that waits for triggers.
     */
    public constructor() {
        this.initializePersistentSyncListener();
    }

    public ngOnDestroy(): void {
        this.persistentSyncSubscription?.unsubscribe();
    }

    /**
     * Sets up the long-running Observable pipeline responsible for background synchronization.
     */
    private initializePersistentSyncListener(): void {
        // Subscribing in the service constructor (a singleton) is the only way to guarantee
        // that the syncRequests.pipe(...) pipeline is running immediately upon application startup.
        // This is required because components rely on calling triggerSyncConcurrent() and
        // having an active listener ready to catch that signal immediately.

        // Prevent re-subscription if already active.
        if (this.persistentSyncSubscription && !this.persistentSyncSubscription.closed) {
            return;
        }

        const longRunningSync$ = this.syncRequests.pipe(
            exhaustMap(() =>
                this.connectivityService.isOnline$.pipe(
                    filter(Boolean),
                    take(1),
                    concatMap(() =>
                        this.syncLocalAndRemoteWithLatestValues().pipe(
                            // Notify components/UI that the synchronization has successfully completed
                            tap(() => this.syncCompleted$.next()),
                            catchError((err) => {
                                this.log.error(
                                    `${this.constructor.name}: initializePersistentSyncListener: Failed to complete sync:`,
                                    err,
                                );
                                return EMPTY;
                            }),
                        ),
                    ),
                ),
            ),
        );

        /**
         * Self-Subscription: Subscribe once to keep the listener permanently active.
         * This is necessary because it manages a core background process.
         */
        this.persistentSyncSubscription = longRunningSync$.subscribe();
    }

    /** Signals the service to initiate background sync. */
    public triggerSyncConcurrent(): void {
        this.syncRequests.next();
    }

    public getOtherTasksByEquipment(
        notificationData: NotificationToGetOtherTasks,
        equipment: string,
    ): Observable<OtherTaskNotificationModel[]> {
        return this.odataOtherTasksService.getNotificationsByEquipment(notificationData, equipment);
    }

    public getOtherTasksByCustomer(
        notificationData: NotificationToGetOtherTasks,
        customer: string,
    ): Observable<OtherTaskNotificationModel[]> {
        return this.odataOtherTasksService.getNotificationsByCustomer(notificationData, customer);
    }

    public getTask(notificationData: NotificationToModifyOtherTask): Observable<NotificationToModifyOtherTask> {
        return this.odataOtherTasksService.getTask(notificationData);
    }

    public saveTaskOriginalPlannedDate(notificationData: NotificationToModifyOtherTask): Observable<void> {
        const formatMomentInUtc = this.configService.getFormatMomentInUTC();
        const plannedStartDateTime = TimeUtilities.momentFromDateAndTime(
            notificationData.PlannedDate,
            notificationData.PlannedStartTime,
            formatMomentInUtc,
        );

        return this.db
            .upsert(
                { notification: notificationData.NotificationNo, task: notificationData.TaskNo },
                { date: plannedStartDateTime.toISOString() },
            )
            .pipe(
                tap(() => this.triggerSyncConcurrent()),
                catchError((error) => {
                    this.log.error(`${this.constructor.name}: failed to upsert planned date:`, error);
                    return EMPTY;
                }),
                map(() => undefined),
            );
    }

    public getTaskOriginalPlannedDate(
        notification: NotificationIdentifierModel,
    ): Observable<moment.Moment | undefined> {
        return this.db
            .getLiveData<OtherTaskOriginalDate>({
                notification: notification.qmNum,
                task: notification.maNum,
            })
            .pipe(
                map((entry) => (entry?.date ? moment(entry.date) : undefined)),
                catchError((e) => {
                    this.log.error(
                        `${this.constructor.name}: failed to get planned date of task ${notification.qmNum}::${notification.maNum}:`,
                        e,
                    );
                    return of(undefined);
                }),
            );
    }

    public getAllPickedTasks(): Observable<string[]> {
        return this.db.getAllEntries().pipe(
            catchError((e) => {
                this.log.error(`${this.constructor.name}: failed to get planned date from IndexedDB:`, e);
                return of([]);
            }),
        );
    }

    public removeTaskOriginalPlannedDate(notification: NotificationIdentifierModel): Observable<void> {
        return this.db.remove({ notification: notification.qmNum, task: notification.maNum }).pipe(
            tap(() => this.triggerSyncConcurrent()),
            catchError((e) => {
                this.log.error(
                    `${this.constructor.name}: failed to remove planned date of task ${notification.qmNum}::${notification.maNum}:`,
                    e,
                );
                return EMPTY;
            }),
            map(() => undefined),
        );
    }

    /**
     * Sync local state with remote kv store
     *
     * The challenge is that dates are stored locally in a table structure
     * (each date as an individual row in IndexedDB), while the remote store
     * maintains all dates as an array under a single key. The remote store
     * only supports get, set, and delete operations per key, requiring
     * client-side merging and synchronization.
     *
     * The current approach is to download the remote data, synchronize it
     * with the local database, compute the updated remote state, and
     * immediately push this new state to the remote key-value store. This
     * reduces the likelihood of conflicts with states from other devices.
     *
     * @returns an observable of the http and computation process
     * @private
     */
    private syncLocalAndRemoteWithLatestValues(): Observable<void> {
        return this.concurrentUsageService.readDataForKey(OtherTasksService.OTHER_TASKS_CONCURRENT_USAGE_KEY).pipe(
            map((remoteData) => remoteData?.[0]?.value ?? ''),
            concatMap((remoteData) => this.db.syncLocalAndRemote<OtherTaskOriginalDate>(remoteData)),
            concatMap((latest) =>
                this.concurrentUsageService
                    .writeDataForKey(OtherTasksService.OTHER_TASKS_CONCURRENT_USAGE_KEY, latest, Date.now())
                    .pipe(map(() => undefined)),
            ),
            catchError((e) => {
                this.log.error(`${this.constructor.name}: error while synchronising other task dates`, e);
                return EMPTY;
            }),
        );
    }
}
